import { useWithdrawalPolicy } from '../../features/settings';
import { fenToYuan, formatDateTime } from '../../lib/format';

export function WithdrawalPolicyPage() {
  const {
    data, loading, saving, error,
    minimumAmountFen, setMinimumAmountFen,
    firstMinimumAmountFen, setFirstMinimumAmountFen,
    dailyLimit, setDailyLimit,
    bankCardEnabled, setBankCardEnabled,
    alipayEnabled, setAlipayEnabled,
    windowEnabled, setWindowEnabled,
    windowStartTime, setWindowStartTime,
    windowEndTime, setWindowEndTime,
    newUserRequiredCompletedTasks, setNewUserRequiredCompletedTasks,
    save, reload,
  } = useWithdrawalPolicy();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <p className="text-sm text-gray-500">
        配置用户首次提现、后续提现的最低金额、每日次数和允许提交提现申请的时间段。
      </p>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            后续提现最低金额（分）
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={0}
              value={minimumAmountFen}
              onChange={(e) => setMinimumAmountFen(Number(e.target.value) || 0)}
              className="h-9 w-60 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
            <span className="text-xs text-gray-400">= ¥{fenToYuan(minimumAmountFen)}</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            首次提现最低金额（分）
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              value={firstMinimumAmountFen}
              onChange={(e) => setFirstMinimumAmountFen(Number(e.target.value) || 0)}
              className="h-9 w-60 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
            />
            <span className="text-xs text-gray-400">= ¥{fenToYuan(firstMinimumAmountFen)}</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            每日提现限制（次）
          </label>
          <input
            type="number"
            min={0}
            value={dailyLimit}
            onChange={(e) => setDailyLimit(Number(e.target.value) || 0)}
            className="h-9 w-60 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            新用户提现前需完成任务数
          </label>
          <input
            type="number"
            min={0}
            step={1}
            value={newUserRequiredCompletedTasks}
            onChange={(e) => setNewUserRequiredCompletedTasks(Math.max(0, Math.trunc(Number(e.target.value) || 0)))}
            className="h-9 w-60 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
          <p className="mt-1 text-xs text-gray-400">
            仅限制本版本上线后注册的用户；0 表示不限制。
          </p>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            可用提现方式
          </label>
          <label className="flex h-9 items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={alipayEnabled}
              onChange={(e) => setAlipayEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50"
            />
            支付宝
          </label>
          <label className="flex h-9 items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={bankCardEnabled}
              onChange={(e) => setBankCardEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50"
            />
            银行卡
          </label>
        </div>

        <div className="space-y-3">
          <label className="flex h-9 items-center gap-2 text-sm font-medium text-gray-700">
            <input
              type="checkbox"
              checked={windowEnabled}
              onChange={(e) => setWindowEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/50"
            />
            启用提款时间限制
          </label>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-gray-700">
              开始时间
              <input
                type="time"
                value={windowStartTime}
                onChange={(e) => setWindowStartTime(e.target.value)}
                disabled={!windowEnabled}
                className="ml-2 h-9 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50"
              />
            </label>
            <label className="text-sm text-gray-700">
              截止时间
              <input
                type="time"
                value={windowEndTime}
                onChange={(e) => setWindowEndTime(e.target.value)}
                disabled={!windowEnabled}
                className="ml-2 h-9 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50"
              />
            </label>
          </div>
          <p className="text-xs text-gray-400">
            启用后仅允许在北京时间 {windowStartTime} 至 {windowEndTime} 提交提现申请，截止时间本身不开放。
          </p>
        </div>

        <div>
          <button
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors"
          >
            {saving && (
              <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            )}
            保存
          </button>
        </div>
      </div>

      {data && (
        <div className="pt-4 border-t border-gray-200 text-xs text-gray-400 space-y-1">
          <p>最后更新：{formatDateTime(data.updated_at)}</p>
          {data.updated_by != null && <p>操作人 ID：{data.updated_by}</p>}
        </div>
      )}
    </div>
  );
}
